import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core'
import { Subject, Observable, interval, of } from 'rxjs'
import { catchError, filter, flatMap, map, take, takeUntil, timeout } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { slideInDoors } from '../../../animations/slideInDoors'
import { slideInOnlyDown } from '../../../animations/slideInOnlyDown'
import { slideInOnlyUp } from '../../../animations/slideInOnlyUp'
import { KeySystemNavConfig } from '../../../configuration/key.system.nav.config'
import {
  Assignment,
  Book,
  Cylinder,
  KeyBitArray,
  KeySystem,
  Library,
  Structure,
  StructureDetails,
} from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingLibraryService } from '../../../services/keying.library.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { Accept, KeyingSystemService } from '../../../services/keying.system.service'
import { KeyingDataSets, KeyingDataSetsService, KeySystemDataSets } from '../../../services/keying.data.sets.service'
import { MasterKey, MasterKeyService, SelectedMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { MasterKeyUpdateService } from '../../../services/masterkey.update.service'
import { KeyingBookService } from '../../../services/keying.book.service'
import { ReserveDialogComponent } from '../../../dialogs/reserve-dialog/reserve-dialog.component'
import { CylinderDataSets, KeyingCylinderService } from '../../../services/keying.cylinder.service'
import { CrossKeyDialogComponent } from '../../../dialogs/cross-key-dialog/cross-key-dialog.component'
import { CrossKeyRangeDialogComponent } from '../../../dialogs/cross-key-range-dialog/cross-key-range-dialog.component'
import { AddSelectiveMasterDialogComponent } from '../../../dialogs/add-selective-master-dialog/add-selective-master-dialog.component'
import { DialogService } from '../../../services/dialog.service'
import { ThruNotationDialogComponent } from '../../../dialogs/thru-notation-dialog/thru-notation-dialog.component'
import { LoggingService } from '../../../services/logging.service'
import { LoadingService } from '../../../services/loading.service'
import { KeyNodeTitle, KeyNodeType } from '@overtur-monorepo/keying-shared'
import { AssignmentService } from '../../../services/assignment.service'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { AppRoutingConfig } from '../../../configuration/app.routing.config'
import { Router } from '@angular/router'
import { CylinderLibraryComponent } from '../../../components/library/cylinder-library/cylinder-library.component'

import { MasterKeyService as KeyingMasterKeyService } from '../../../services/keying.masterkey.service'
import { MoveKeysDialogComponent } from '../../../dialogs/move-keys-dialog/move-keys-dialog.component'
import { RenameKeysDialogComponent } from '../../../dialogs/rename-keys-dialog/rename-keys-dialog.component'
import { ChangeKeyTypeDialogComponent } from '../../../dialogs/change-key-type-dialog/change-key-type-dialog.component'
import { ChangeLogConfirmationDialogComponent } from '../../../components/matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { MatDialogState } from '@angular/material/dialog'
import { MoveBittingsDialogComponent, MoveBittingsDialogResult } from '../../../dialogs/move-bittings-dialog/move-bittings-dialog.component'

@Component({
  selector: 'app-hierarchy',
  templateUrl: './hierarchy.component.html',
  styleUrls: ['./hierarchy.component.scss'],
  animations: [slideInOnlyDown, slideInDoors, fadeIn, slideInOnlyUp],
  standalone: false,
})
export class HierarchyComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<boolean>()
  keyNodeType = KeyNodeType

  structure: Structure
  keySystem
  keyingDataSets: KeySystemDataSets
  masterKeyProfile: KeySystem
  selectedMasterKey: MasterKey
  loading = true
  selectedBook: Book
  selectedKbaArray = []
  selectedForMoveBittings = []
  assignedMasterKeyBittings = []
  reservationTypes = []
  selectedCylinder: Cylinder
  cylinderNav = false
  cylinderValidation
  cylinderDataSets: CylinderDataSets
  library: Library
  keyway
  hasControlKeyMaster = false
  controlKeyMasterAssigned = false
  contentType: Accept
  selectedFilterKey
  competitorPinning

  delayTime
  keyRenamed = false

  request

  hierarchySideBar = {
    show: true,
    width: '0px',
  }
  mkSideBar = {
    show: true,
    width: '292px',
  }

  private isCrossKeySelected = false

  public tabs = [
    { name: 'Keys', id: 0 },
    { name: 'Cylinders', id: 1 },
  ]
  public selectedTab = this.tabs[0]
  public selectedTabIndex = 0

  private newKeyName: string
  private onNewKeyCompleted: any

  @ViewChild(CylinderLibraryComponent) cylinderLibrary: CylinderLibraryComponent

  @Output() prepareForMECS = new EventEmitter<boolean>()
  enablePrepareForMECS = false

  constructor(
    private selectedMasterKeyService: SelectedMasterKeyService,
    private keyingSystemService: KeyingSystemService,
    private masterKeyService: MasterKeyService,
    private keyingStructureService: KeyingStructureService,
    private keyingLibraryService: KeyingLibraryService,
    private errorService: ErrorService,
    private keySystemNavConfig: KeySystemNavConfig,
    private masterKeyUpdateService: MasterKeyUpdateService,
    private keyingBookService: KeyingBookService,
    private keyingDataSetsService: KeyingDataSetsService,
    private keyingCylinderService: KeyingCylinderService,
    private dialogService: DialogService,
    private loggingService: LoggingService,
    private loadingService: LoadingService,
    private assignmentService: AssignmentService,
    private router: Router,
    private keyingMasterKeyService: KeyingMasterKeyService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  /* istanbul ignore next */
  onAddKey({ keyName, competitorInfo, onCompleted }) {
    this.newKeyName = keyName
    this.competitorPinning = competitorInfo
    this.onNewKeyCompleted = onCompleted
    this.contentType = Accept.AddSplKey
    this.checkIfCylinderIsPublished()
  }

  /* istanbul ignore next */
  onRemoveKey({ keyName, onCompleted }) {
    this.newKeyName = keyName
    this.onNewKeyCompleted = onCompleted
    this.contentType = Accept.RemoveSplKey
    this.checkIfCylinderIsPublished()
  }

  /* istanbul ignore next */
  private checkIfCylinderIsPublished() {
    if (this.cylinderLibrary?.selectedCylinders?.find((m) => m.isPublished || m.isLegacyPublished)) {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        actionType: 'Key Updated',
        showCancel: true,
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: this.newKeyName,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.onConfirmAddOrRemovekey(result.auditTrailRequest)
        }
      })
    } else {
      this.onConfirmAddOrRemovekey()
    }
  }

  /* istanbul ignore next */
  private onConfirmAddOrRemovekey(auditTrailRequest: any = undefined) {
    const cylinderIds = this.cylinderLibrary?.selectedCylinders?.map((cylinder) => cylinder.id)
    const structureId = this.structure.id
    const keySystemId = this.keySystem.id
    this.keyingSystemService
      .addOrRemoveSpecialFunctionKey(
        { keySetSymbol: this.newKeyName, competitorId: this.competitorPinning, cylinderIds },
        { structureId, keySystemId },
        this.contentType,
        auditTrailRequest
      )
      .subscribe((result) => {
        if (this.onNewKeyCompleted) {
          const afterDone$: Observable<any> = this.onNewKeyCompleted(result)
          if (afterDone$) {
            afterDone$.subscribe(() => {
              if (this.newKeyName === 'CK') {
                this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
              }
              this.cylinderLibrary.goToLibrary(true)
              this.cylinderLibrary.clearCylindersSelection()
            })
          }
        }
      })
  }

  /* istanbul ignore next */
  isNonNull<T>(value: T): value is NonNullable<T> {
    return value != null
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    // Subscribe to double-click assign key events from the hierarchy tree
    this.masterKeyService.assignKey$.pipe(takeUntil(this.destroy$)).subscribe((masterKey: MasterKey) => {
      this.assignSingleKey(masterKey)
    })
  }

  /* istanbul ignore next */
  ngAfterViewInit(): void {
    this.keyingDataSetsService.structureDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((details: StructureDetails) => {
        if (details?.mecsInProgress || details?.publishingInProgress || details?.pinningGenerationInprogress) {
          this.router.navigate([AppRoutingConfig.structuresUrl])
        }
      })

    this.keyingStructureService.structure$
      .pipe(
        filter(this.isNonNull),
        takeUntil(this.destroy$),
        flatMap((structure: Structure) => {
          this.structure = structure
          return this.keyingDataSetsService.keySystemDataSets$
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (keyingDataSets) => {
          if (keyingDataSets) {
            this.keySystem = keyingDataSets.keySystem
            this.keyingDataSets = keyingDataSets

            this.hasControlKeyMaster = keyingDataSets.masterKeysKeying.children.some(
              (m) => m.masterKeyTypeId === this.keyNodeType.ControlKeyMaster && m.children.length > 0
            )
          }
          if (!this.keySystem || !this.structure) {
            return
          }

          if (this.structure && this.keySystem && this.keySystem?.topMasterKey) {
            this.keySystemNavConfig.setNav('Key System', this.structure, this.keySystem)

            if (this.selectedMasterKey) {
              let key = this.keyingSystemService.getMasterKeyById(
                this.keyingDataSets.masterKeysKeying,
                this.selectedMasterKey.id
              )

              this.selectedMasterKeyService.setSelectedMasterKey(key)
            }

            // if key has been renamed, refresh the library
            if (this.keyRenamed) {
              this.keyRenamed = false
              this.loadingService.set({ isLoading: false, message: '' })
              // load library
              this.keyingBookService.loadFormattedCombinations(
                this.structure,
                this.keySystem.id,
                this.selectedMasterKey
              )

              this.keyingSystemService
                .getKeySystems(this.structure.id)
                .pipe(take(1))
                .subscribe((keySystems) => {
                  this.structure.keySystems = keySystems
                  this.keyingStructureService.selectStructure(this.structure)
                })
            }
          } else {
            this.router.navigate([
              AppRoutingConfig.getGenericUrl('structureBudget', this.structure.id, this.keySystem.id),
            ])
          }
          this.cdr.markForCheck()
        },
        (error) => {
          this.errorService.throw(error)
        }
      )

    // Selected MasterKey
    this.selectedMasterKeyService.selectedMasterKey$
      .pipe(takeUntil(this.destroy$))
      .subscribe((masterKey: MasterKey) => {
        if (!masterKey) {
          return
        }
        masterKey.crossKeyAssignments = []
        let tmk = masterKey.tmk()
        let crossKeys = tmk
          .squashIncludeChangeKeys()
          .filter((k) => k.masterKeyTypeId === this.keyNodeType.CrossKeyChild)
        let crossKeyOps = masterKey.children.filter((key) => key.isCrossKeyOperator)
        crossKeys.forEach((crossKey) => {
          crossKeyOps.forEach((crosskeyOp) => {
            crossKey.children.forEach((k) => {
              if (k.id === crosskeyOp.id) {
                if (!masterKey.crossKeyAssignments.includes(crossKey)) {
                  masterKey.crossKeyAssignments.push(crossKey)
                }
              }
            })
          })
        })
        this.selectedMasterKey = masterKey
        if (this.selectedMasterKey) {
          this.filterKeyListUnassigned()
        }

        this.loggingService.log('Selected Key:', this.selectedMasterKey)
        this.cdr.markForCheck()
      })

    // Subscribe to Selected Book
    this.keyingLibraryService.selectedBook$.pipe(takeUntil(this.destroy$)).subscribe((book: Book) => {
      this.selectedBook = book
      this.cdr.markForCheck()
    })

    // Keying Data Sets
    this.keyingDataSetsService.keyingDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets) {
          return
        }
        this.reservationTypes = keyingDataSets.reservationTypes
        this.cdr.markForCheck()
      })

    // Bitting Data Sets
    this.keyingBookService.bittingDataSets$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res || !this.selectedMasterKey) {
        return
      }

      this.selectedMasterKey.children.forEach((child) => {
        child.assignment = new Assignment()
      })

      this.library = res.library
      this.loggingService.log('New Library Selected', this.library.books)

      if (this.selectedBook) {
        this.keyingLibraryService.selectBook(this.library.books.find((book) => book.id === this.selectedBook.id))
      } else if (this.library.books && this.library.books.length == 1) {
        this.keyingLibraryService.selectBook(this.library.books[0])
      } else {
        this.keyingLibraryService.selectBook(null)
      }
      this.loadingService.set({ isLoading: false, message: '' })
      this.cdr.markForCheck()
    })

    // Cylinders
    this.keyingCylinderService.cylinderDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cylinderDataSets: CylinderDataSets) => {
        this.cylinderDataSets = cylinderDataSets
        this.cdr.markForCheck()
      })

    this.keyingMasterKeyService.masterKeyUpdated$.pipe(takeUntil(this.destroy$)).subscribe(({ data }) => {
      if (data?.action === 'cross') {
        this.tabs = [{ name: 'Cylinders', id: 1 }]
        this.onChangeTab(this.tabs[0])
      }
    })

    this.keyingSystemService.deletedMasterKey$.pipe(takeUntil(this.destroy$)).subscribe((deletedMasterKey) => {
      if (deletedMasterKey) {
        let isMasterKeyFound = false
        let i = 0,
          j = 0

        for (i = 0; i < this.keyingDataSets.masterKeysKeying.children.length; i++) {
          const parent = this.keyingDataSets.masterKeysKeying.children[i]
          if (isMasterKeyFound) {
            break
          }

          for (j = 0; j < parent.children.length; j++) {
            const child = parent.children[j]
            if (child.id === deletedMasterKey.id) {
              isMasterKeyFound = true
              break
            }
          }
        }

        if (isMasterKeyFound) {
          if (i > this.keyingDataSets.masterKeysKeying.children.length - 1) {
            i = this.keyingDataSets.masterKeysKeying.children.length - 1
          }
          this.keyingDataSets.masterKeysKeying.children[i].children.splice(j, 1)
        }
      }
    })
    this.setDefaultWidth()

    // Force a CD cycle after all BehaviorSubject replays have fired synchronously.
    // Without this, markForCheck() dirty marks are never processed because the HTTP
    // response that would trigger ApplicationRef.tick() may have already completed.
    this.ngZone.run(() => setTimeout(() => this.cdr.markForCheck(), 0))
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.unsubscribe()
    this.masterKeyService.setMasterKeys(null)
    this.keyingLibraryService.selectBook(null)
    this.selectedMasterKeyService.setSelectedMasterKey(null)
  }

  trackTab(_index: number, tab: { id: number }): number {
    return tab.id
  }

  /* istanbul ignore next */
  onChangeTab(event: any): void {
    const previousTabId = this.selectedTab?.id
    this.selectedTabIndex = event.index
    this.selectedTab = this.tabs[event.index] // Update selectedTab based on current index

    // Skip loading if tab didn't actually change (can happen when tabs array is reassigned)
    if (previousTabId === this.selectedTab?.id) {
      return
    }

    // When switching to Keys tab (id=0), load library for current selection
    if (
      this.selectedTab?.id === 0 &&
      this.selectedMasterKey &&
      !this.keySystem.isDealerKeySystem &&
      this.selectedMasterKey.masterKeyTypeId !== this.keyNodeType.CrossKeyChild &&
      this.selectedMasterKey.masterKeyTypeId !== this.keyNodeType.ConstructionKey
    ) {
      this.loadingService.set({ isLoading: true, message: 'Getting Library' })
      this.keyingBookService.loadFormattedCombinations(this.structure, this.keySystem.id, this.selectedMasterKey)
    }
    // Cylinder loading is handled by cylinder-library component via ngOnInit
  }

  /* istanbul ignore next */
  // onChangeTab(event) {
  //   this.selectedTab = this.tabs.find((tab) => tab.id === event.index)
  //   if (this.selectedTab.id === 1 && !this.cylinderDataSets.cylinders.length) {
  //     this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)
  //   }
  // }

  /* istanbul ignore next */
  setDefaultWidth() {
    if (this.hierarchySideBar.width === '0px') {
      this.hierarchySideBar.width = document.getElementById('hierarchySideBar').scrollWidth + 'px'
    }
  }

  /* istanbul ignore next */
  updateRequest(request) {
    this.request = request
  }

  unassignKbas() {
    if (this.selectedKbaArray.length === 0) {
      this.selectedKbaArray = this.selectedBook.allBittings.filter((bitting) => !bitting.disabled && !bitting.hidden)
    }

    let data = {
      masterkeys: this.selectedMasterKey.children.filter((child) => child.inFilter),
      selectedKbaArray: this.selectedKbaArray,
      selectedMasterKey: this.selectedMasterKey,
      selectedBook: this.selectedBook,
      assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      rules: {
        skipGuidelineViolators: true,
        skipReserved: false,
      },
    }

    this.selectedKbaArray.map((kba) => {
      kba.selected = false
      this.deselectParent(kba)
    })
    this.assignmentService.unassignKbas(data)
    this.assignedMasterKeyBittings = this.assignmentService.prepareAssignmentRequest(data)
    this.selectedMasterKey.children.map((child) => {
      if (child.inFilter || (child.unassignedLocally && !child.isAssignedLocally && !child.isFutureKey)) {
        child.inFilter = true
      } else {
        child.inFilter = false
      }
    })
    this.selectedKbaArray = []
  }

  /* istanbul ignore next */
  resetBook() {
    this.keyingBookService.loadFormattedCombinations(this.structure, this.keySystem.id, this.selectedMasterKey)
    this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
    this.loadCylindersConditionally(this.selectedMasterKey)
    this.selectedKbaArray.map((kba) => {
      kba.selected = false
      this.deselectParent(kba)
    })
    this.selectedKbaArray = []
    this.assignedMasterKeyBittings = []
  }

  /* istanbul ignore next */
  reloadBook() {
    this.keyingBookService.loadFormattedCombinations(
      this.structure,
      this.keySystem.id,
      this.selectedMasterKey,
      this.assignedMasterKeyBittings,
      this.selectedBook,
      this.keyingDataSets.masterKeysKeying
    )
    this.loadCylindersConditionally(this.selectedMasterKey)
    this.selectedKbaArray.map((kba) => {
      kba.selected = false
      this.deselectParent(kba)
    })
    this.selectedKbaArray = []
  }

  private getMasterKeys() {
    let filteredMasterKeys: MasterKey[] = []
    for (let i = 0; i < this.selectedMasterKey.children.length; i++) {
      const child = this.selectedMasterKey.children[i]
      if (
        this.structure.combinatingRuleSetId === 7 &&
        child.masterKeyTypeId === this.keyNodeType.ControlKeyMaster &&
        !child.isAssigned
      ) {
        filteredMasterKeys.push(child)
        break
      } else if (child.inFilter) {
        filteredMasterKeys.push(child)
      }
    }

    return filteredMasterKeys
  }

  assignKbas(skipGuidelineViolators = true) {
    if (this.selectedKbaArray.length === 0) {
      this.selectedKbaArray = this.selectedBook.allBittings.filter((bitting) => !bitting.disabled && !bitting.hidden)
    }
    const controlKey = this.keyingDataSets.masterKeysKeying.children.find(
      (m) => m.masterKeyTypeId === this.keyNodeType.ControlKeyMaster
    )

    let data = {
      masterkeys: this.getMasterKeys(),
      selectedKbaArray: this.selectedKbaArray,
      selectedMasterKey: this.selectedMasterKey,
      selectedBook: this.selectedBook,
      assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      rules: {
        skipGuidelineViolators: skipGuidelineViolators,
        skipReserved: true,
      },
    }

    this.selectedKbaArray.map((kba) => {
      kba.selected = false
      this.deselectParent(kba)
    })
    this.assignmentService.assignKbas(data)
    this.assignedMasterKeyBittings = this.assignmentService.prepareAssignmentRequest(data)
    this.selectedKbaArray = []
  }

  /* istanbul ignore next */
  assignSingleKey(masterKey: MasterKey) {
    // Check if we have a selected book (required for assignment)
    if (!this.selectedBook) {
      this.errorService.throw({ message: 'Please select a book first to assign keys' })
      return
    }

    // Check if the key is already assigned in DB and not locally touched
    if (masterKey.isAssigned && !masterKey.unassignedLocally) {
      this.errorService.throw({ message: 'This key is already assigned' })
      return
    }

    // Toggle: if locally assigned this session, double-click again unassigns it
    if (masterKey.assignment?.kba) {
      const kba = masterKey.assignment.kba as unknown as KeyBitArray
      kba.assignment = new Assignment()
      kba.selected = false
      this.deselectParent(kba)
      masterKey.assignment = new Assignment()
      if (masterKey.isAssigned) {
        masterKey.unassignedLocally = true
      }
      masterKey.isAssignedLocally = false
      masterKey.inFilter = (!masterKey.isAssigned || masterKey.unassignedLocally) && !masterKey.isAssignedLocally && !masterKey.isFutureKey
      this.assignedMasterKeyBittings = this.assignmentService.prepareAssignmentRequest({
        selectedBook: this.selectedBook,
        assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      })
      return
    }

    // If user has selected specific available bittings, use those; otherwise use all available
    const selectedAvailable = this.selectedKbaArray.filter(
      (b) => !b.disabled && !b.hidden && !b.assignment?.masterKey?.id
    )
    const targetBittings =
      selectedAvailable.length > 0
        ? [selectedAvailable[0]]
        : this.selectedBook.allBittings.filter((b) => !b.disabled && !b.hidden)

    // Use the same assignment logic as assignBittings in format.data.service
    let data = {
      masterkeys: [masterKey],
      selectedKbaArray: targetBittings,
      selectedMasterKey: this.selectedMasterKey,
      selectedBook: this.selectedBook,
      assignedMasterKeyBittings: this.assignedMasterKeyBittings,
      rules: {
        skipGuidelineViolators: true,
        skipReserved: true,
      },
    }

    // Perform assignment using the existing service
    this.assignmentService.assignKbas(data)
    this.assignedMasterKeyBittings = this.assignmentService.prepareAssignmentRequest(data)
  }

  /* istanbul ignore next */
  assignCrossKeyKbas() {}

  /* istanbul ignore next */
  goToLibrary() {
    this.filterKeyListUnassigned()
    this.keyingLibraryService.selectBook(null)
  }

  /* istanbul ignore next */
  goToCylLibrary() {
    this.cylinderNav = true
  }

  /* istanbul ignore next */
  cylNavComplete() {
    this.cylinderNav = false
  }

  /* istanbul ignore next */
  filterKeyList(crossKey) {
    this.selectedFilterKey = crossKey

    this.selectedMasterKey.children.map((child) => {
      let key = crossKey.children.find((ckc) => child.id === ckc.id)

      if (key) {
        child.inFilter = true
      } else {
        child.inFilter = false
      }
    })
  }

  /* istanbul ignore next */
  filterKeyListUnassigned() {
    this.selectedFilterKey = 'Unassigned Keys'

    this.selectedMasterKey.children.map((child) => {
      if (!child.isAssigned && !child.isAssignedLocally && !child.isFutureKey) {
        child.inFilter = true
      } else {
        child.inFilter = false
      }
    })
  }

  /* istanbul ignore next */
  clearFilter() {
    this.selectedFilterKey = 'All Keys'
    this.selectedMasterKey.children.forEach((child) => {
      if (!child.isFutureKey) {
        child.inFilter = true
      }
    })
  }

  /* istanbul ignore next */
  setAssignedMasterKeyBittings(event) {
    this.assignedMasterKeyBittings = event
  }

  /* istanbul ignore next */
  splitMasterKeyBitting(bitting) {
    this.reloadBook()
  }

  /* istanbul ignore next */
  selectCylinder(cylinder: Cylinder) {
    this.selectedCylinder = cylinder
    this.cylNavComplete()
  }

  /* istanbul ignore next */
  deselectParent(item) {
    if (item.parent && item.parent.selected) {
      item.parent.selected = false
      this.deselectParent(item.parent)
    }
  }

  /* istanbul ignore next */
  selectedKbas(kbas) {
    this.selectedKbaArray = kbas
  }

  /* istanbul ignore next */
  onSelectedForMoveChanged(bittings) {
    this.selectedForMoveBittings = bittings
  }

  /* istanbul ignore next */
  openMoveBittingsDialog() {
    const masterKeysRoot = this.keyingDataSets?.masterKeysKeying
    const allKeys = masterKeysRoot ? masterKeysRoot.squash() : []
    const allowedTypeIds = [1, 5, 7, 9]
    const masterKeys = allKeys.filter((key) => allowedTypeIds.includes(key.masterKeyTypeId))

    const dialogData = {
      sourceBookId: this.selectedBook?.id,
      selectedBittings: this.selectedForMoveBittings,
      keySystemId: this.keySystem?.id,
      masterKeyId: this.selectedMasterKey?.id,
      masterKeys: masterKeys,
    }

    const dialogRef = this.dialogService.open(MoveBittingsDialogComponent, dialogData, false)

    dialogRef.afterClosed().subscribe((result: MoveBittingsDialogResult) => {
      if (result) {
        this.handleMoveBittings(result)
      }
    })
  }

  /* istanbul ignore next */
  private handleMoveBittings(result: MoveBittingsDialogResult) {
    this.loggingService.log('Move Bittings Result', result)

    const request = {
      sourceBookId: this.selectedBook?.id,
      bittings: result.selectedBittings.map((b) => b.masterKeyBitting),
    }

    if (result.destinationType === 'new') {
      this.keyingBookService
        .moveBittingsToNewBook(this.keySystem.id, result.destinationKeyId, request)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.loggingService.log('Move Bittings', response)
            this.errorService.notify('Success', response)
            this.selectedForMoveBittings = []
            this.keyingBookService.loadFormattedCombinations(this.structure, this.keySystem.id, this.selectedMasterKey)
          },
          error: (error) => {
            this.errorService.throw(error)
          },
        })
    } else {
      this.keyingBookService
        .moveBittingsToExistingBook(this.keySystem.id, this.selectedMasterKey?.id, result.destinationBookId, request)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.loggingService.log('Move Bittings', response)
            this.errorService.notify('Success', response)
            this.selectedForMoveBittings = []
            this.keyingBookService.loadFormattedCombinations(this.structure, this.keySystem.id, this.selectedMasterKey)
          },
          error: (error) => {
            this.errorService.throw(error)
          },
        })
    }
  }

  loadCylindersConditionally(masterKey) {
    // cylinder-library component handles its own loading via selectedMasterKey$ subscription
    // No need to load cylinders from hierarchy - this prevents unnecessary API calls on Keys tab
    // and avoids duplicate calls on Cylinders tab
    return
  }

  selectMasterKey(masterKey: MasterKey) {
    this.library = new Library()
    this.selectedMasterKeyService.setSelectedMasterKey(masterKey)
    this.keyingLibraryService.selectBook(null)
    this.assignedMasterKeyBittings = []
    this.selectedKbaArray = []
    this.cylinderValidation = null

    /**
     * Reset local state flags on all children to clear stale state from previous operations.
     * Without this reset, flags like unassignedLocally/isAssignedLocally persist in memory
     * even after the book reloads from DB, causing incorrect UI state and unassign behavior.
     * See: docs/features/DOUBLE_CLICK_KEY_ASSIGNMENT.md for bug fix details.
     */
    masterKey.children?.forEach((child) => {
      child.unassignedLocally = false
      child.isAssignedLocally = false
    })

    this.loadCylindersConditionally(masterKey)

    // Only load library if on Keys tab (id=0) - cylinder-library handles its own loading
    if (
      this.selectedTab?.id !== 1 &&
      !this.keySystem.isDealerKeySystem &&
      masterKey.masterKeyTypeId !== this.keyNodeType.CrossKeyChild &&
      masterKey?.masterKeyTypeId !== this.keyNodeType.ConstructionKey
    ) {
      this.loadingService.set({ isLoading: true, message: 'Getting Library' })
      this.keyingBookService.loadFormattedCombinations(this.structure, this.keySystem.id, masterKey)
    }

    // show-hide tabs
    if (masterKey.masterKeyTypeId === this.keyNodeType.CrossKeyChild) {
      this.tabs = [{ name: 'Cylinders', id: 1 }]
      this.selectedTab = this.tabs[0]
      this.isCrossKeySelected = true
    } else if (
      masterKey.masterKeyTypeId === this.keyNodeType.DirectChangeMaster ||
      masterKey.masterKeyTypeId === this.keyNodeType.SingleKeyDifferentMaster ||
      masterKey.masterKeyTypeId === this.keyNodeType.ControlKeyMaster
    ) {
      this.tabs = [{ name: 'Keys', id: 0 }]
      this.selectedTab = this.tabs[0]
    } else if (masterKey?.masterKeyTypeId === this.keyNodeType.Master) {
      this.tabs = [
        { name: 'Keys', id: 0 },
        { name: 'Cylinders', id: 1 },
      ]
      // Only reset to the Keys tab when coming from a key type that had a completely
      // different tab set (e.g. ConstructionKey/SelectiveMaster use a single 'Key' tab
      // with id=2). Do NOT reset when coming from CrossKeyChild (id=1, Cylinders only)
      // — the user was already viewing the Cylinders tab and expects it to remain active
      // so that the regular master's cylinders load immediately without an extra click.
      if (this.selectedTab.id > 1) {
        this.selectedTab = this.tabs[0]
      }
    } else if (
      masterKey?.masterKeyTypeId === this.keyNodeType.ConstructionKey ||
      masterKey?.masterKeyTypeId === this.keyNodeType.SelectiveMaster
    ) {
      this.tabs = [{ name: 'Key', id: 2 }]
      this.selectedMasterKey.keyway = this.getKeyway()
      this.selectedTab = this.tabs[0]
    }

    if (masterKey.masterKeyTypeId !== this.keyNodeType.CrossKeyChild) {
      this.isCrossKeySelected = false
    }
    // Loading is cleared in bittingDataSets$ when the library HTTP response arrives.
  }

  autoSet(id, min) {
    const width = document.getElementById(id).style.width

    if (this[id].show && width && Number(width.replace('px', '')) < Number(min.replace('px', ''))) {
      this[id].show = false
      document.getElementById(id).style.width = '11px'
    } else if (!this[id].show) {
      this[id].show = true
      document.getElementById(id).style.width = this[id].width
    }
  }

  /* istanbul ignore next */
  getStringAfterSubstring(parentString, subString) {
    return parentString.substring(parentString.indexOf(subString) + subString.length)
  }

  /* istanbul ignore next */
  getStringBeforeSubstring(parentString, subString) {
    return parentString.substring(0, parentString.indexOf(subString))
  }

  /* istanbul ignore next */
  deleteItem(masterKey) {
    if (masterKey.isPublished || masterKey.isLegacyPublished) {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        actionType: 'Key Deleted',
        showCancel: true,
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: masterKey.keySetSymbol,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.deleteKeys(masterKey, result.auditTrailRequest)
        }
      })
    } else {
      this.deleteKeys(masterKey)
    }
  }

  /* istanbul ignore next */
  deleteKeys(masterKey, auditTrailRequest: any = undefined) {
    if (masterKey.id === this.selectedMasterKey.id) {
      this.selectMasterKey(masterKey.parent)
    }

    if (
      masterKey.keySetSymbol.includes('-') &&
      masterKey.masterKeyTypeId !== this.keyNodeType.DirectChangeMaster &&
      masterKey.masterKeyTypeId !== this.keyNodeType.SingleKeyDifferentMaster
    ) {
      this.deleteChildren({
        structure: this.structure,
        keySystem: this.keySystem,
        masterKey: masterKey.parent,
        action: 'DELETE_KEYS',
      })
    } else {
      this.masterKeyUpdateService.deleteMasterKey(this.structure, this.keySystem, masterKey, auditTrailRequest)
    }
  }

  /* istanbul ignore next */
  deleteChildren(deleteData) {
    this.openDialog(ThruNotationDialogComponent, deleteData, true)
  }

  /* istanbul ignore next */
  patchItem(patchData) {
    if (this.keySystem.isPreviouslyPublished && patchData.isDummy && patchData.type === 'dummyTmk') {
      const data = {
        cancel: 'Cancel',
        confirm: 'Confirm',
        actionType: 'Dummy TMK',
        showCancel: true,
        reasonRequired: false,
        keySystemId: this.keySystem.id,
        source: patchData.masterKey.keySetSymbol,
      }
      data.reasonRequired = true
      const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, data, false)

      dialogRef.afterClosed().subscribe((result) => {
        if (result.confirmed) {
          this.masterKeyUpdateService.patchItem(
            this.structure,
            this.keySystem,
            patchData,
            this.selectedMasterKey,
            result.auditTrailRequest
          )
        }
      })
    } else {
      this.masterKeyUpdateService.patchItem(this.structure, this.keySystem, patchData, this.selectedMasterKey)
    }
  }

  addChild(data) {
    switch (data.action) {
      case 'cross':
        this.openDialog(CrossKeyDialogComponent, data, true)
        break
      case 'CROSS_KEY_RANGE':
        data.structure = this.structure
        data.keySystem = this.keySystem
        this.openDialog(CrossKeyRangeDialogComponent, data, true)
        break
      case 'SLM':
        this.openDialog(AddSelectiveMasterDialogComponent, data, true)
        break
      case 'CHANGE_KEYS':
      case 'MASTER_KEYS':
        data.structure = this.structure
        data.keySystem = this.keySystem
        this.openDialog(ThruNotationDialogComponent, data, true)
        break
      case 'MOVE_KEYS':
        data.structure = this.structure
        data.keySystem = this.keySystem
        this.openDialog(MoveKeysDialogComponent, data, true)
        break
      case 'RENAME_KEYS':
        data.structure = this.structure
        data.keySystem = this.keySystem
        this.openDialog(RenameKeysDialogComponent, data, true)
        break
      case 'CHANGE_KEY_TYPE':
        data.structure = this.structure
        data.keySystem = this.keySystem
        this.openDialog(ChangeKeyTypeDialogComponent, data, true)
        break
      default:
        this.masterKeyUpdateService.addChild(this.structure, this.keySystem, data).subscribe()
        break
    }
  }

  /* istanbul ignore next */
  findNodeById(root: any, id: number | string): any | undefined {
    if (!root) return undefined
    if (String(root.id) === String(id)) return root
    if (Array.isArray(root.children)) {
      for (const c of root.children) {
        const found = this.findNodeById(c, id)
        if (found) return found
      }
    }
    return undefined
  }

  /* istanbul ignore next */
  openDialog(dialog, data?, autoFocus = false): void {
    switch (dialog) {
      case 1:
        dialog = ReserveDialogComponent
        break
      default:
        break
    }

    let dialogRef = this.dialogService.open(dialog, data, autoFocus)

    dialogRef.afterClosed().subscribe((request) => {
      if (request) {
        switch (dialog) {
          case ReserveDialogComponent:
            this.selectedKbaArray.map((kba) => {
              kba.selected = false
              this.deselectParent(kba)
            })
            data.selectedBook = this.selectedBook
            data.rules = { skipGuidelineViolators: false }
            this.assignedMasterKeyBittings = this.assignmentService.prepareAssignmentRequest(data)
            break
          case CrossKeyDialogComponent:
            this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe({
              next: (response) => {
                const check$ = interval(300).pipe(
                  // check every 300ms for new key in the dataset
                  map(() => {
                    const root = this.keyingDataSets?.masterKeysKeying
                    return this.findNodeById(root, response.id)
                  }),
                  filter((found) => !!found),
                  take(1),
                  timeout(20000), // timeout after 20 seconds, if new key is not located in dataset
                  catchError((err) => {
                    console.warn('Timed out waiting for new key', response.id)
                    return of(null)
                  })
                )

                check$.subscribe((foundNode) => {
                  if (foundNode) {
                    this.selectedMasterKeyService.setSelectedMasterKey(foundNode)
                  } else {
                    console.warn('Could not find new node within timeout; consider using response directly.')
                  }
                })
              },
              error: (err) => console.error(err),
            })
            break
          case CrossKeyRangeDialogComponent:
            this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe({
              next: (response) => {
                // Response is BulkCrossKeyResultDto from backend
                if (response && response.totalFailed > 0) {
                  // Show error dialog if there are failures
                  const failedList = response.failedCylinders
                    .map((fc) => `${fc.keySetSymbol}: ${fc.errorMessage || 'Unknown error'}`)
                    .join('\n')
                  const confirmData = {
                    title: 'Bulk Cross Key Creation Results',
                    message: `Created ${response.totalSuccessful} of ${response.totalRequested} cross keyed cylinders.\n\nFailed cylinders:\n${failedList}`,
                    confirm: 'OK',
                    showCancel: false,
                  }
                  this.dialogService.open(ConfirmDialogComponent, confirmData, false)
                } else if (response && response.totalSuccessful > 0) {
                  // All successful - show success message
                  const confirmData = {
                    title: 'Success',
                    message: `Successfully created ${response.totalSuccessful} cross keyed cylinders.`,
                    confirm: 'OK',
                    showCancel: false,
                  }
                  this.dialogService.open(ConfirmDialogComponent, confirmData, false)
                }
              },
              error: (err) => {
                console.error('Error creating bulk cross keys:', err)
                this.errorService.throw(err)
              },
            })
            break
          case ThruNotationDialogComponent:
            if (request.action === KeyNodeTitle.CHANGE_KEYS || request.action === KeyNodeTitle.MASTER_KEYS) {
              this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe()
            } else if (request.action === KeyNodeTitle.DELETE_KEYS) {
              this.masterKeyUpdateService.deleteChildren(
                this.structure,
                this.keySystem,
                request,
                this.selectedMasterKey
              )
            }
            break
          case MoveKeysDialogComponent:
            this.keyingMasterKeyService
              .moveKeys(this.structure.id, this.keySystem.id, request)
              .pipe(take(1))
              .subscribe(() => {
                this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)

                this.keyingSystemService
                  .getKeySystems(this.structure.id)
                  .pipe(take(1))
                  .subscribe((keySystems) => {
                    this.structure.keySystems = keySystems
                    this.keyingStructureService.selectStructure(this.structure)
                  })
              })
            break
          case RenameKeysDialogComponent:
            this.keyingMasterKeyService
              .renameKeys(this.structure.id, this.keySystem.id, request)
              .pipe(take(1))
              .subscribe(() => {
                this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)

                this.loadingService.set({ isLoading: true, message: 'Updating Library' })
                //Set key to renamed so the rest of the affiliated data can refresh (books/bittings) in the afterviewinit
                this.keyRenamed = true
              })
            break
          case ChangeKeyTypeDialogComponent:
            this.keyingMasterKeyService
              .changeKeyType(this.structure.id, this.keySystem.id, request)
              .pipe(take(1))
              .subscribe(() => {
                this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)

                this.keyingSystemService
                  .getKeySystems(this.structure.id)
                  .pipe(take(1))
                  .subscribe((keySystems) => {
                    this.structure.keySystems = keySystems
                    this.keyingStructureService.selectStructure(this.structure)
                  })
              })
            break
          case AddSelectiveMasterDialogComponent:
            this.masterKeyUpdateService.addChild(this.structure, this.keySystem, request).subscribe()
            break
          default:
            break
        }
      }
    })
  }

  /* istanbul ignore next */
  getKeyway() {
    if (this.selectedMasterKey && this.selectedMasterKey.keywayId && this.structure) {
      this.keyingDataSetsService
        .getKeyway(this.structure.keywayFamilyId, this.selectedMasterKey.keywayId)
        .pipe(take(1))
        .subscribe((keyway) => (this.keyway = keyway))
    } else {
      this.keyway = undefined
    }
  }

  /* istanbul ignore next */
  validateCylinder() {
    this.loadingService.set({ isLoading: true, message: 'Validating Cylinder' })
    this.keyingCylinderService
      .validateCylinder(
        this.structure.id,
        this.keySystem.id,
        this.selectedCylinder.masterKeyId,
        this.selectedCylinder.id
      )
      .subscribe(
        (res: Cylinder) => {
          this.loggingService.log('Response:', res)
          this.cylinderValidation = res
          this.keyingDataSetsService.loadKeySystemDataSets(this.structure.id, this.keySystem.id)
          this.keyingCylinderService.loadCylinders(this.structure, this.keySystem.id, this.selectedMasterKey.id)
          this.keyingDataSetsService.loadStructureDetails(this.structure.id)
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: '' })
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  prepareForMECSHandler() {
    this.enablePrepareForMECS = true
  }

  /* istanbul ignore next */
  hotkey(type) {
    this.dialogService.serviceDialogRef$.pipe(take(1)).subscribe((dialogRef) => {
      if (!dialogRef || dialogRef.getState() === MatDialogState.CLOSED) {
        switch (type) {
          case 'u':
            this.unassignKbas()
            break
          case 'a':
            this.assignKbas()
            break
          case 'g':
            this.assignKbas(false)
            break
          case 'r':
            this.openDialog(1, {
              masterkeys: this.selectedMasterKey.children,
              selectedKbaArray: this.selectedKbaArray,
              selectedMasterKey: this.selectedMasterKey,
              reservationTypes: this.reservationTypes,
            })
            break
        }
      }
    })
  }
}
